/* ═══════════════════════════════════════════════════════════════════
   BLISS · FREQUENCY — Sound engine
   Two engines share one AudioContext:
     • AIR  — pink-noise breath (reused pattern from divineSounds)
     • VESSEL — pure-tone singing-bowl breath at fixed Hz
       (432 · 528 · 741 · 852 · 963), volume envelope low→high→low→void.
   Pure Web Audio API — zero cloud cost.
   ─────────────────────────────────────────────────────────────────── */
import { getAudioCtx } from './audioCore';


let _ctx = null;
let _activeSession = null;

const getCtx = () => {
  if (typeof window === 'undefined') return null;
  _ctx = getAudioCtx();
  return _ctx;
};

/* ── Pink noise buffer builder (used only by AIR engine) ───────────── */
const buildNoiseBuffer = (ctx, seconds = 20) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return buf;
};

/* ── Chime (session start / end marker) ───────────────────────────── */
export const startChime = () => {
  const ctx = getCtx();
  if (!ctx) return;
  const partials = [
    { f: 440, gain: 0.20, decay: 3.4 },
    { f: 660, gain: 0.13, decay: 3.0, delay: 0.03 },
    { f: 880, gain: 0.07, decay: 2.4, delay: 0.05 },
  ];
  partials.forEach(({ f, gain, decay, delay = 0 }) => {
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    osc.connect(g).connect(ctx.destination);
    osc.start(t); osc.stop(t + decay + 0.2);
  });
};
export const endChime = () => {
  startChime();
  setTimeout(() => startChime(), 1100);
};

/* ══════════════════════════════════════════════════════════════════
   AIR — PURE WIND breath
   ─────────────────────────────────────────────────────────────────
   Previously a lowpass pink-noise (~sea/ocean feel).  Rebuilt as a
   BAND-passed higher-mid noise with a slight upper-shelf sparkle and
   a slow LFO on the filter to give it a real "breeze" character —
   no sub-bass rumble (which is what created the ocean impression),
   crisp airy sibilance instead.

   Volume envelope has 6 stops per user spec:
     0 → LOW → MEDIUM → HIGH → MEDIUM → LOW → VOID
   spread evenly across the tone duration.
   ══════════════════════════════════════════════════════════════════ */
const scheduleAirSegment = (ctx, noiseBuf, startAt, dur, peak = 0.32) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  src.loop = true;

  // High-pass to strip sub-bass rumble (kills "ocean" feel).
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.setValueAtTime(220, startAt);
  hp.Q.value = 0.3;

  // Bandpass sits around ~800 Hz — where wind through leaves lives.
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(800, startAt);
  bp.Q.value = 0.55;

  // Subtle high-shelf sparkle at ~3 kHz for airy sibilance.
  const hs = ctx.createBiquadFilter();
  hs.type = 'highshelf';
  hs.frequency.setValueAtTime(3000, startAt);
  hs.gain.setValueAtTime(4, startAt);

  // LFO modulating the bandpass centre → living breeze motion.
  const lfo = ctx.createOscillator();
  const lfoDepth = ctx.createGain();
  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(0.18, startAt); // ~5.5 s per breath cycle
  lfoDepth.gain.value = 260;
  lfo.connect(lfoDepth).connect(bp.frequency);

  // 6-stop dynamic envelope (LOW → MED → HIGH → MED → LOW → VOID).
  const g = ctx.createGain();
  const t = startAt;
  g.gain.setValueAtTime(0.0001,          t);
  g.gain.linearRampToValueAtTime(peak * 0.30, t + dur * 0.15); // LOW
  g.gain.linearRampToValueAtTime(peak * 0.60, t + dur * 0.32); // MEDIUM
  g.gain.linearRampToValueAtTime(peak * 1.00, t + dur * 0.50); // HIGH crest
  g.gain.linearRampToValueAtTime(peak * 0.60, t + dur * 0.68); // MEDIUM
  g.gain.linearRampToValueAtTime(peak * 0.30, t + dur * 0.85); // LOW
  g.gain.linearRampToValueAtTime(0.0001,      t + dur);         // VOID

  src.connect(hp).connect(bp).connect(hs).connect(g).connect(ctx.destination);
  src.start(t);
  lfo.start(t);
  src.stop(t + dur + 0.1);
  lfo.stop(t + dur + 0.1);
  return [src, lfo];
};

/* ══════════════════════════════════════════════════════════════════
   VESSEL — FLOATING THERAPY BOWL
   ─────────────────────────────────────────────────────────────────
   Previously two detuned sines + octave → sounded like a struck rod.
   Rebuilt with:
     • soft exponential attack (as if the bowl is being rubbed, not hit)
     • fundamental + soft octave-down (grounding warmth)
     • gentle upper octave (halo shimmer)
     • Tibetan-signature inharmonic partial at ~2.76 × fundamental
       (very quiet — gives "bowl" character without harshness)
     • slow 0.35 Hz tremolo on the master gain → floating breath
     • lowpass @ 3 kHz to smooth any harmonic edge
   All layers use exponential ramps so no clicks / no rod-strike.
   ══════════════════════════════════════════════════════════════════ */
const scheduleVesselSegment = (ctx, hz, startAt, dur, peak = 0.28) => {
  const t = startAt;
  const sources = [];

  // Master gain for the whole segment (tremolo modulates this).
  const masterG = ctx.createGain();
  masterG.gain.setValueAtTime(0.0001, t);
  masterG.gain.exponentialRampToValueAtTime(1.0, t + dur * 0.55);   // slow rise
  masterG.gain.setValueAtTime(1.0,                t + dur * 0.65);
  masterG.gain.exponentialRampToValueAtTime(0.0001, t + dur);       // slow fade

  // Warm lowpass @ 3 kHz — smooths any harshness.
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(3000, t);
  lp.Q.value = 0.4;

  masterG.connect(lp).connect(ctx.destination);

  // Tremolo LFO — floating "breath" motion.
  const trem = ctx.createOscillator();
  const tremDepth = ctx.createGain();
  trem.type = 'sine';
  trem.frequency.setValueAtTime(0.35, t);
  tremDepth.gain.value = 0.08;
  trem.connect(tremDepth).connect(masterG.gain);
  trem.start(t);
  trem.stop(t + dur + 0.1);
  sources.push(trem);

  // ── Layer 1 · fundamental (single sine, no detune for purity) ──
  {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, t);
    g.gain.value = peak * 0.55;
    osc.connect(g).connect(masterG);
    osc.start(t);
    osc.stop(t + dur + 0.1);
    sources.push(osc);
  }

  // ── Layer 2 · sub-octave (0.5x fundamental) — floating grounding warmth ──
  {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz * 0.5, t);
    g.gain.value = peak * 0.20;
    osc.connect(g).connect(masterG);
    osc.start(t);
    osc.stop(t + dur + 0.1);
    sources.push(osc);
  }

  // ── Layer 3 · upper octave (2x) — quiet halo shimmer ──
  {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz * 2, t);
    g.gain.value = peak * 0.10;
    osc.connect(g).connect(masterG);
    osc.start(t);
    osc.stop(t + dur + 0.1);
    sources.push(osc);
  }

  // ── Layer 4 · Tibetan-signature inharmonic partial (2.76x) ──
  {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz * 2.76, t);
    g.gain.value = peak * 0.05;   // very quiet — gives bowl fingerprint
    osc.connect(g).connect(masterG);
    osc.start(t);
    osc.stop(t + dur + 0.1);
    sources.push(osc);
  }

  return sources;
};

/**
 * Start a Frequency session.
 *
 * @param {object} opts
 * @param {'air'|'vessel'} opts.mode
 * @param {number}  opts.hz            — vessel frequency (only used for mode='vessel')
 * @param {number}  opts.toneSec       — length of tone/air phase (default 30)
 * @param {number}  opts.voidSec       — length of void phase (default 30)
 * @param {number}  opts.totalMinutes  — total session length in minutes (default 10)
 * @param {(state) => void} opts.onTick
 * @param {() => void} opts.onComplete
 * @returns {{ stop: () => void }}
 */
export const startFrequencySession = ({
  mode = 'vessel',
  hz = 432,
  toneSec = 30,
  voidSec = 30,
  totalMinutes = 10,
  onTick = () => {},
  onComplete = () => {},
} = {}) => {
  stopFrequencySession(); // ensure only one active
  const ctx = getCtx();
  if (!ctx) return { stop: () => {} };
  try { ctx.resume?.(); } catch { /* noop */ }

  const cycleSec = toneSec + voidSec;
  const totalSec = totalMinutes * 60;
  const totalCycles = Math.max(1, Math.floor(totalSec / cycleSec));
  const t0 = ctx.currentTime + 0.15;

  const sources = [];
  const noiseBuf = mode === 'air' ? buildNoiseBuffer(ctx, toneSec + 2) : null;

  for (let i = 0; i < totalCycles; i++) {
    const toneStart = t0 + i * cycleSec;
    if (mode === 'air') {
      sources.push(...scheduleAirSegment(ctx, noiseBuf, toneStart, toneSec));
    } else {
      sources.push(...scheduleVesselSegment(ctx, hz, toneStart, toneSec));
    }
  }

  // Opening chime
  startChime();

  // UI clock follows the AUDIO clock (minus output latency) so visuals match what is heard.
  const heardElapsed = () => Math.max(0, ctx.currentTime - (ctx.outputLatency || ctx.baseLatency || 0) - t0);
  const runFor = totalCycles * cycleSec;
  const tickIv = setInterval(() => {
    const elapsed = heardElapsed();
    if (elapsed >= runFor) {
      onTick({ phase: 'void', cycleIndex: totalCycles - 1, secondsIntoPhase: voidSec, done: true });
      stopFrequencySession();
      endChime();
      onComplete();
      return;
    }
    const cycleIndex = Math.floor(elapsed / cycleSec);
    const intoCycle = elapsed - cycleIndex * cycleSec;
    const phase = intoCycle < toneSec ? 'tone' : 'void';
    const secondsIntoPhase = phase === 'tone' ? intoCycle : intoCycle - toneSec;
    const totalRemaining = runFor - elapsed;
    onTick({
      phase, cycleIndex, secondsIntoPhase,
      elapsedSec: elapsed, remainingSec: totalRemaining,
      totalCycles, done: false,
    });
  }, 250);

  _activeSession = {
    sources, tickIv,
    stop() {
      clearInterval(tickIv);
      sources.forEach(s => { try { s.stop(); } catch { /* noop */ } });
    },
  };

  return { stop: stopFrequencySession };
};

export const stopFrequencySession = () => {
  if (_activeSession) {
    try { _activeSession.stop(); } catch { /* noop */ }
    _activeSession = null;
  }
};

/* ── Divine Lexicon: 5 vessel frequencies + metadata ──────────────── */
export const VESSELS = [
  {
    id: 'peace',   hz: 432, intent: 'Peace',
    color: '#7DD3C0', // mint teal
    principle: 'Known as the natural frequency — promotes deep grounding and calmness.',
  },
  {
    id: 'love',    hz: 528, intent: 'Love',
    color: '#FF9EB8', // rose
    principle: 'Known as the Miracle tone — associated with transformation and heart-centered resonance.',
  },
  {
    id: 'success', hz: 741, intent: 'Success',
    color: '#FFB347', // amber
    principle: 'Associated with solving problems and waking intuition for clear action.',
  },
  {
    id: 'wealth',  hz: 852, intent: 'Wealth',
    color: '#C58BF2', // violet
    principle: 'Associated with awakening intuition and returning to spiritual order.',
  },
  {
    id: 'divine',  hz: 963, intent: 'Divine',
    color: '#FFE9A8', // ivory gold
    principle: 'Known as the Frequency of the Gods — for high-level connectivity and inner vision.',
  },
];
